import type { RequestEventData } from '@sentry/vue'
import { redactSentryUrl } from './redactApiPath'

// `beforeSend` helper for Sentry.init (wired in main.ts).
//
// @sentry/vue fills `event.request.url` from `window.location.href` on every
// captured event. That is the PAGE url, and on Stemne some page URLs are
// credentials: `/videorom?url=<whereby participant room URL>&name=…&
// bookingId=…&token=<booking magic token>` carries a bearer capability for
// the video room AND the token that authenticates the booking (built by
// buildStemneParticipantRoomPath in ./whereby.ts and by
// ManageView.handleJoin).
//
// The existing beforeSend scrubbing never touched it: it matches breadcrumb
// data KEY NAMES (/password|totp|token|csrf|secret/i) and `request.headers`,
// and `url` is neither. `internal/sentryutil` does have a full scrub policy
// (sensitiveQueryParams, scrubURL) but it only runs on Go-captured events —
// frontend envelopes go through `/api/sentry-tunnel`, and
// `sentryutil.TunnelHandler` is a byte-for-byte pass-through that validates
// the DSN host and forwards the envelope unmodified. So this has to happen
// client-side, before transport.
//
// Sibling of redactBreadcrumb.ts: same redactor, different carrier (the
// event's own request object rather than the breadcrumb trail). Lives in its
// own module for the same reason that one does — main.ts mounts the app on
// import, so nothing in it is reachable from a unit test.

/**
 * Scrub credentials out of an event's request URL and query string, in place.
 *
 * Returns the same object so a caller can chain; a missing request is passed
 * straight back.
 */
export function redactSentryRequest(request?: RequestEventData): RequestEventData | undefined {
  if (!request) return request

  const url = typeof request.url === 'string' ? redactSentryUrl(request.url) : ''
  if (url) request.url = url

  // `query_string` is optional and rarely set by the browser SDK, but it is a
  // second copy of the same secrets when it is. Only the string form is
  // handled: the object/array forms are keyed, so the existing key-name
  // scrubbing in beforeSend would need a different fix, and neither shape has
  // ever been observed from this SDK.
  if (typeof request.query_string === 'string' && request.query_string) {
    request.query_string = redactQueryString(url || currentPath(), request.query_string)
  }

  return request
}

// The page path to scope the query-string rules to when the event carried no
// usable `url` of its own.
//
// Without a path the route-scoped `/videorom` rules cannot fire, and the two
// values they exist for — the Whereby room URL (a bearer capability for the
// meeting) and the customer's own name — pass straight through. Only the
// `?token=` rule survives a missing path, because it is anchored on a
// parameter boundary rather than on a route. So a `query_string` arriving
// beside an absent `url` was the one shape that still shipped the room key.
//
// `window.location` is the right substitute rather than a guess: the SDK
// fills `request.url` from `window.location.href` in the first place, so on
// any event that has one the two agree, and on an event that has none this is
// the same page the query string was captured on.
//
// Falling back to the CURRENT PATH rather than applying the /videorom rules
// unconditionally keeps the scoping the rules were given deliberately —
// redactApiPath explains that a blanket `url=` redaction would cost triage
// signal on any future return-to link. Guarded for a non-browser global
// (SSR, a bare test environment), where an empty base restores exactly the
// previous behaviour.
function currentPath(): string {
  if (typeof window === 'undefined' || !window.location) return ''
  return window.location.pathname
}

// A bare query string has no path, and both rules that matter need one: the
// `?token=` rule is anchored on a `?`/`&` boundary (a leading `token=` at
// position 0 would not match), and the `/videorom` `?url=` rule is scoped to
// its route. So splice the query back onto the path it came from, redact the
// whole URL, and take the query half back.
function redactQueryString(url: string, query: string): string {
  const cut = url.search(/[?#]/)
  const base = cut === -1 ? url : url.slice(0, cut)
  const redacted = redactSentryUrl(`${base}?${query}`)
  // The separator we just added is the first `?` in the result: neither
  // placeholder contains one, and an origin cannot.
  const separator = redacted.indexOf('?')
  return separator === -1 ? redacted : redacted.slice(separator + 1)
}
